// packages/web-ui/src/hooks/useLlmImageEnabled.ts
//
// Reactive read of the `views.llmImage.enabled` master toggle from the shared
// merged-settings store (the SAME store `useContextWindowForModel` reads, kept
// fresh by `App.tsx`'s `loadMergedSettings()`). Mirrors the pure
// `readLlmImageEnabled()` in `sse/attach-to-chat-slice.ts`, but as a hook that
// re-renders when the setting changes so the Views-tab "Generate LLM
// visualization" affordance appears / disappears live.
//
// Defaults to `false` on any absence / malformed entry so the raster affordance
// is NEVER offered when the setting can't be confirmed ON — the OFF-by-default
// precedence rule (IV2-S2) is enforced here as well as at the chat card.

import { useStore } from 'zustand';
import { qualifiedKey } from '@tessa-lean/base';
import { getDefaultSettingsStore, selectMergedByKey } from '../state/settings.js';

/**
 * React hook — returns the effective `views.llmImage.enabled` master toggle.
 * `true` only when the merged setting resolves to boolean `true`; `false`
 * otherwise (missing / loading / malformed).
 */
export function useLlmImageEnabled(): boolean {
  const store = getDefaultSettingsStore();
  const entry = useStore(store, (s) =>
    selectMergedByKey(s, qualifiedKey('views.llmImage.enabled')),
  );
  if (entry === undefined) return false;
  const raw = entry.value !== undefined ? entry.value : entry.defaultValue;
  return raw === true;
}
